import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { e2eAgentApi } from "./e2eAgentClient";
import type { components } from "./e2eAgentSchema";

export type WorkspaceIdentity = components["schemas"]["WorkspaceIdentityOut"];
export type UpdateWorkspaceProfile =
  components["schemas"]["UpdateWorkspaceProfileRequest"];

export const WORKSPACE_SESSION_QUERY_KEY = [
  "e2e-agent",
  "workspace-session",
] as const;

export function useWorkspaceSession() {
  return useQuery({
    queryKey: WORKSPACE_SESSION_QUERY_KEY,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/workspace/session");
      if (error !== undefined) {
        throw new Error(
          `Workspace session request failed: ${JSON.stringify(error)}`,
        );
      }
      if (data === undefined) {
        throw new Error("Workspace session response was empty.");
      }
      return data;
    },
  });
}

export function useUpdateWorkspaceProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateWorkspaceProfile) => {
      const { data, error } = await e2eAgentApi.PATCH("/v1/workspace/profile", {
        body,
      });
      if (error !== undefined) {
        throw new Error(
          `Workspace profile update failed: ${JSON.stringify(error)}`,
        );
      }
      if (data === undefined) {
        throw new Error("Workspace profile response was empty.");
      }
      return data;
    },
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: WORKSPACE_SESSION_QUERY_KEY }),
  });
}
